'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, CheckCircle2, XCircle, Eye, EyeOff, ChevronRight, ChevronLeft, AlertTriangle, User, CreditCard } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { trackFormSubmitted } from '@/components/MetaPixel';

type Phase = 'phase1' | 'qualifying' | 'result_pass' | 'result_fail' | 'password_setup' | 'profile_setup';

interface Phase1Values {
  full_name: string;
  email: string;
  country: string;
  wise_tag: string;
  username: string;
  job_interest: 'yes' | 'no' | '';
}

interface PasswordValues {
  password: string;
  confirm_password: string;
}

interface ProfileSetupValues {
  username: string;
  wise_tag: string;
}

interface QualifyingQuestion {
  id: string;
  question_text: string;
  correct_option: string;
  // shuffled display options for this applicant
  shuffledOptions: { key: string; text: string; originalKey: string }[];
}

// Confidentiality acknowledgment state (lives in qualifying phase)
type ConfidentialityAnswer = 'yes' | 'no' | '';

const COUNTRIES = [
  'Philippines', 'United States', 'United Kingdom', 'Canada', 'Australia', 'Japan', 'South Korea',
  'India', 'Germany', 'France', 'Italy', 'Brazil', 'Mexico',
  'Indonesia', 'Vietnam', 'Other',
];

// Phase 1 steps definition
const PHASE1_STEPS_BASE = [
  { id: 'full_name', title: 'What is your full name?' },
  { id: 'email', title: 'What is your email address?' },
  { id: 'country', title: 'Which country are you based in?' },
  { id: 'wise_tag', title: 'What is your Wise tag?' },
  { id: 'username', title: 'Choose your username' },
  { id: 'job_interest', title: 'Full-time role (optional)' },
];

// Fisher-Yates shuffle
function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const LETTER_KEYS = ['A', 'B', 'C', 'D', 'E', 'F'];

export default function ApplicationFormFlow() {
  const [phase, setPhase] = useState<Phase>('phase1');
  const [phase1Step, setPhase1Step] = useState(0);
  const [phase1Data, setPhase1Data] = useState<Phase1Values | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleSigningIn, setIsGoogleSigningIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [googleUserEmail, setGoogleUserEmail] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [deviceFingerprint, setDeviceFingerprint] = useState<string>('');
  const [ipLockoutUntil, setIpLockoutUntil] = useState<string | null>(null);

  // Qualifying phase state
  const [qualifyingQuestions, setQualifyingQuestions] = useState<QualifyingQuestion[]>([]);
  const [qualifyingStep, setQualifyingStep] = useState(0);
  const [qualifyingAnswers, setQualifyingAnswers] = useState<Record<string, string>>({});
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const questionsLoadedRef = useRef(false);

  // Confidentiality acknowledgment (shown as first step of qualifying phase)
  const [confidentialityAnswer, setConfidentialityAnswer] = useState<ConfidentialityAnswer>('');
  const [showConfidentialityStep, setShowConfidentialityStep] = useState(true);

  // Form version for consent record
  const [formVersion, setFormVersion] = useState(1);

  const supabase = createClient();
  const router = useRouter();
  const { signOut, signInWithGoogle } = useAuth();

  const phase1Form = useForm<Phase1Values>({ defaultValues: { job_interest: '' } });
  const passwordForm = useForm<PasswordValues>();
  const profileSetupForm = useForm<ProfileSetupValues>();

  // Build phase1 steps — skip wise_tag and username for Google users
  const phase1Steps = PHASE1_STEPS_BASE.filter((s) => {
    if (isGoogleUser && (s.id === 'wise_tag' || s.id === 'username')) return false;
    return true;
  });

  const totalSteps = phase1Steps.length;

  // Build a localStorage key scoped to the user
  const getStorageKey = (userId: string) => `wb_app_progress_${userId}`;

  // Save progress to localStorage
  const saveProgress = (userId: string, currentPhase: Phase, p1Data: Phase1Values | null) => {
    try {
      const payload = { phase: currentPhase, phase1Data: p1Data };
      localStorage.setItem(getStorageKey(userId), JSON.stringify(payload));
    } catch { /* ignore */ }
  };

  // Clear saved progress from localStorage
  const clearProgress = (userId: string) => {
    try { localStorage.removeItem(getStorageKey(userId)); } catch { /* ignore */ }
  };

  // Load qualifying questions from Supabase
  const loadQualifyingQuestions = async () => {
    if (questionsLoadedRef.current) return;
    setLoadingQuestions(true);
    try {
      // Get current version
      const { data: configData } = await supabase
        .from('form_config')
        .select('value')
        .eq('key', 'current_version')
        .single();

      const currentVersion = configData ? parseInt(configData.value, 10) : 1;
      setFormVersion(currentVersion);

      // Load qualifying questions for current version
      const { data: rows } = await supabase
        .from('form_questions')
        .select('*')
        .eq('form_version', currentVersion)
        .eq('type', 'qualifying')
        .order('display_order', { ascending: true });

      if (rows && rows.length > 0) {
        const questions: QualifyingQuestion[] = rows.map((r) => {
          const rawOptions: { key: string; text: string }[] = Array.isArray(r.options) ? r.options : [];
          // Shuffle the options, reassign display letters A/B/C/D
          const shuffled = shuffleArray(rawOptions);
          const shuffledOptions = shuffled.map((opt, idx) => ({
            key: LETTER_KEYS[idx] || String(idx + 1),
            text: opt.text,
            originalKey: opt.key,
          }));
          return {
            id: r.id,
            question_text: r.question_text,
            correct_option: r.correct_option || 'A',
            shuffledOptions,
          };
        });
        setQualifyingQuestions(questions);
        questionsLoadedRef.current = true;
      } else {
        // No qualifying questions — skip qualifying phase
        questionsLoadedRef.current = true;
        setQualifyingQuestions([]);
      }
    } catch {
      questionsLoadedRef.current = true;
      setQualifyingQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Generate a stable browser fingerprint from available signals
  const generateFingerprint = (): string => {
    if (typeof window === 'undefined') return 'unknown';
    const signals = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || 0,
      (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0,
    ].join('|');
    // Simple hash
    let hash = 0;
    for (let i = 0; i < signals.length; i++) {
      const char = signals.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  };

  // Check IP + fingerprint lockout via API
  const checkIpFingerprintLockout = async (fp: string): Promise<string | null> => {
    try {
      const res = await fetch('/api/application-lockout/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fingerprint: fp }),
      });
      const data = await res.json();
      if (data.locked) return data.locked_until as string;
    } catch { /* ignore */ }
    return null;
  };

  // Record IP + fingerprint lockout via API
  const recordIpFingerprintLockout = async (fp: string): Promise<void> => {
    try {
      await fetch('/api/application-lockout/record', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fingerprint: fp }),
      });
    } catch { /* ignore */ }
  };

  // Detect if user is already signed in via Google (OAuth)
  useEffect(() => {
    const checkUser = async () => {
      try {
        // Generate fingerprint early
        const fp = generateFingerprint();
        setDeviceFingerprint(fp);

        // Check IP/fingerprint lockout first
        const lockoutUntil = await checkIpFingerprintLockout(fp);
        if (lockoutUntil) {
          setIpLockoutUntil(lockoutUntil);
          setCheckingAuth(false);
          return;
        }

        if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          const code = urlParams.get('code');
          if (code) {
            try {
              await supabase.auth.exchangeCodeForSession(code);
            } catch {
              // ignore exchange errors
            }
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
          }
        }

        const { data: { user }, error } = await supabase.auth.getUser();
        if (error || !user) return;

        setCurrentUserId(user.id);
        const provider = user.app_metadata?.provider;
        const isGoogle = provider === 'google';
        setIsGoogleUser(isGoogle);

        if (isGoogle) {
          const email = user.email || '';
          setGoogleUserEmail(email);
          const fullName = user.user_metadata?.full_name || user.user_metadata?.name || '';

          let restored = false;
          try {
            const saved = localStorage.getItem(getStorageKey(user.id));
            if (saved) {
              const parsed = JSON.parse(saved) as {
                phase: Phase;
                phase1Data: Phase1Values | null;
              };
              if (parsed.phase === 'phase1') {
                const p1 = parsed.phase1Data || { full_name: fullName, email, country: '', wise_tag: '', username: '', job_interest: '' as const };
                p1.full_name = fullName || p1.full_name;
                p1.email = email || p1.email;
                phase1Form.reset(p1);
                setPhase1Data(p1);
                setPhase(parsed.phase);
                restored = true;
              }
            }
          } catch { /* ignore */ }

          if (!restored) {
            phase1Form.reset({ full_name: fullName, email, country: '', wise_tag: '', username: '', job_interest: '' });
          }
        } else {
          const email = user.email || '';
          let restored = false;
          try {
            const saved = localStorage.getItem(getStorageKey(user.id));
            if (saved) {
              const parsed = JSON.parse(saved) as {
                phase: Phase;
                phase1Data: Phase1Values | null;
              };
              if (parsed.phase === 'phase1') {
                const p1 = parsed.phase1Data || { full_name: '', email, country: '', wise_tag: '', username: '', job_interest: '' as const };
                phase1Form.reset(p1);
                setPhase1Data(p1);
                setPhase(parsed.phase);
                restored = true;
              }
            }
          } catch { /* ignore */ }

          if (!restored) {
            phase1Form.reset({ full_name: '', email, country: '', wise_tag: '', username: '', job_interest: '' });
          }
        }
      } catch {
        // Silently handle any unexpected errors
      } finally {
        setCheckingAuth(false);
      }
    };
    checkUser();
  }, []);

  const applyLockout = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const lockoutUntil = new Date();
    lockoutUntil.setDate(lockoutUntil.getDate() + 7);
    await supabase.from('user_profiles').update({ application_status: 'rejected', lockout_until: lockoutUntil.toISOString() }).eq('id', user.id);
    // Also record IP + fingerprint lockout
    await recordIpFingerprintLockout(deviceFingerprint);
  };

  // Validate current phase1 step field and advance
  const handlePhase1Next = async () => {
    const currentStepId = phase1Steps[phase1Step].id as keyof Phase1Values;
    const valid = await phase1Form.trigger(currentStepId);
    if (!valid) return;

    // job_interest is the last phase1 step — both answers pass, just record and move on
    if (currentStepId === 'job_interest') {
      const data = phase1Form.getValues();
      setPhase1Data(data);
      if (currentUserId) clearProgress(currentUserId);

      setIsLoading(true);
      await loadQualifyingQuestions();
      setIsLoading(false);

      // Reset confidentiality state for this run
      setConfidentialityAnswer('');
      setShowConfidentialityStep(true);
      setQualifyingStep(0);
      setQualifyingAnswers({});
      setPhase('qualifying');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Save progress on each phase1 step advance
    if (currentUserId) {
      const p1Data = phase1Form.getValues();
      saveProgress(currentUserId, 'phase1', p1Data);
    }

    if (phase1Step < totalSteps - 1) {
      setPhase1Step((s) => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePhase1Back = () => {
    if (phase1Step > 0) {
      setPhase1Step((s) => s - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle answer selection for current qualifying question
  const handleQualifyingSelect = (questionId: string, selectedOriginalKey: string) => {
    setQualifyingAnswers((prev) => ({ ...prev, [questionId]: selectedOriginalKey }));
  };

  // Handle confidentiality step submission
  const handleConfidentialityNext = () => {
    if (!confidentialityAnswer) return;

    if (confidentialityAnswer === 'no') {
      // Disqualify — same as wrong quiz answer
      applyLockout();
      setPhase('result_fail');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // "yes" — passed confidentiality gate, move to quiz questions
    setShowConfidentialityStep(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Advance to next qualifying question or evaluate
  const handleQualifyingNext = () => {
    const currentQ = qualifyingQuestions[qualifyingStep];
    if (!qualifyingAnswers[currentQ.id]) return; // must select an answer

    if (qualifyingStep < qualifyingQuestions.length - 1) {
      setQualifyingStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // All questions answered — evaluate
      const allCorrect = qualifyingQuestions.every(
        (q) => qualifyingAnswers[q.id] === q.correct_option
      );

      if (allCorrect) {
        // Store consent + job interest on profile
        storeApplicationAnswers();
        if (isGoogleUser) {
          setPhase('profile_setup');
        } else {
          setPhase('result_pass');
        }
        trackFormSubmitted(
          { form: 'qualifying_quiz', result: 'pass' },
          {
            email: phase1Data?.email ?? googleUserEmail,
            name: phase1Data?.full_name,
          }
        );
      } else {
        applyLockout();
        setPhase('result_fail');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Store job_interest and confidentiality consent on the user profile
  const storeApplicationAnswers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await supabase.from('user_profiles').update({
        job_interest: phase1Data?.job_interest || phase1Form.getValues('job_interest') || 'no',
        confidentiality_agreed: true,
        confidentiality_agreed_at: new Date().toISOString(),
        confidentiality_form_version: formVersion,
      }).eq('id', user.id);
    } catch { /* ignore — non-critical */ }
  };

  const handleQualifyingBack = () => {
    if (!showConfidentialityStep) {
      if (qualifyingStep > 0) {
        setQualifyingStep((s) => s - 1);
      } else {
        // Back to confidentiality step
        setShowConfidentialityStep(true);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Back to last phase1 step
      setPhase('phase1');
      setPhase1Step(totalSteps - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePasswordSubmit = async (_data: PasswordValues) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1000));
    setIsLoading(false);
    window.location.href = '/member-dashboard';
  };

  const handleProfileSetupSubmit = async (data: ProfileSetupValues) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      await supabase.from('user_profiles').update({ username: data.username, wise_tag: `@${data.wise_tag}`, application_status: 'approved', lockout_until: null }).eq('id', user.id);
      if (currentUserId) clearProgress(currentUserId);
      router.push('/member-dashboard');
    } catch {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      // ignore sign out errors
    }
    router.push('/sign-up-login-screen');
  };

  // Overall progress calculation
  // Qualifying phase now includes: 1 confidentiality step + N quiz questions
  const qualifyingTotal = 1 + qualifyingQuestions.length; // confidentiality + quiz
  const totalPhases = qualifyingTotal > 0 ? totalSteps + qualifyingTotal : totalSteps;
  const currentProgress = phase === 'phase1'
    ? phase1Step
    : phase === 'qualifying'
    ? showConfidentialityStep
      ? totalSteps
      : totalSteps + 1 + qualifyingStep
    : totalPhases;
  const progressPct = Math.round((currentProgress / totalPhases) * 100);

  if (checkingAuth) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  // ── IP/Fingerprint lockout screen ─────────────────────────────────────────
  if (ipLockoutUntil) {
    const lockoutDate = new Date(ipLockoutUntil);
    const now = new Date();
    const diffMs = lockoutDate.getTime() - now.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const timeStr = days > 0 ? `${days}d ${hours}h` : `${hours}h`;

    return (
      <div className="bg-card rounded-2xl border border-border p-8 sm:p-12 text-center animate-fade-in">
        <div className="w-20 h-20 bg-danger/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle size={40} className="text-danger" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-3">Application Temporarily Locked</h2>
        <p className="text-muted-foreground mb-4 max-w-md mx-auto leading-relaxed">
          A recent application from this device did not meet our requirements. You must wait before applying again.
        </p>
        <div className="bg-warning/10 border border-warning/20 rounded-xl p-4 mb-8 max-w-sm mx-auto">
          <div className="flex items-center gap-2 justify-center">
            <AlertTriangle size={16} className="text-warning flex-shrink-0" />
            <p className="text-sm text-warning-foreground font-medium">
              You may re-apply in approximately {timeStr}.
            </p>
          </div>
        </div>
        <Link href="/" className="inline-flex items-center gap-2 border border-border text-foreground px-6 py-2.5 rounded-xl font-medium hover:bg-muted transition-colors">
          Return to Homepage
        </Link>
      </div>
    );
  }

  // ── Profile Setup (Google users who passed) ──────────────────────────────
  if (phase === 'profile_setup') {
    return (
      <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 animate-fade-in">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
            <CheckCircle2 size={22} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-foreground">You qualified! Set up your profile</h2>
            <p className="text-xs text-muted-foreground">One last step before you start competing</p>
          </div>
        </div>
        <form onSubmit={profileSetupForm.handleSubmit(handleProfileSetupSubmit)} className="space-y-5">
          <div>
            <label htmlFor="ps-username" className="block text-sm font-medium text-foreground mb-1.5">
              <span className="flex items-center gap-1.5"><User size={14} /> Username <span className="text-danger">*</span></span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">Unique, lowercase, no spaces. Visible on the leaderboard.</p>
            <input
              id="ps-username"
              type="text"
              className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${profileSetupForm.formState.errors.username ? 'border-danger' : 'border-input focus:border-primary'}`}
              placeholder="kaito_prompts"
              {...profileSetupForm.register('username', {
                required: 'Username is required',
                pattern: { value: /^[a-z0-9_]+$/, message: 'Lowercase letters, numbers, and underscores only' },
                minLength: { value: 3, message: 'At least 3 characters' },
                maxLength: { value: 30, message: 'Maximum 30 characters' },
              })}
            />
            {profileSetupForm.formState.errors.username && (
              <p className="mt-1.5 text-xs text-danger">{profileSetupForm.formState.errors.username.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="ps-wise-tag" className="block text-sm font-medium text-foreground mb-1.5">
              <span className="flex items-center gap-1.5"><CreditCard size={14} /> Wise tag <span className="text-danger">*</span></span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">Your Wise payment handle. Required for payouts. Stored encrypted.</p>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground select-none font-medium">@</span>
              <input
                id="ps-wise-tag"
                type="text"
                className={`w-full pl-8 pr-3.5 py-2.5 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${profileSetupForm.formState.errors.wise_tag ? 'border-danger' : 'border-input focus:border-primary'}`}
                placeholder="yourwisename"
                {...profileSetupForm.register('wise_tag', {
                  required: 'Wise tag is required',
                  pattern: { value: /^[a-zA-Z0-9._-]+$/, message: 'Enter your Wise ID without the @ symbol' },
                })}
              />
            </div>
            {profileSetupForm.formState.errors.wise_tag && (
              <p className="mt-1.5 text-xs text-danger">{profileSetupForm.formState.errors.wise_tag.message}</p>
            )}
          </div>
          <div className="bg-muted/40 rounded-xl p-4 text-sm text-muted-foreground">
            Signed in as <span className="font-medium text-foreground">{googleUserEmail}</span> via Google
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 px-4 rounded-xl text-sm font-semibold hover:bg-secondary transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ minHeight: '44px' }}
          >
            {isLoading ? <><Loader2 size={16} className="animate-spin" />Setting up your account...</> : <>Complete Setup &amp; Start Competing <ChevronRight size={16} /></>}
          </button>
        </form>
      </div>
    );
  }

  // ── Result: Pass (non-Google users) ──────────────────────────────────────
  if (phase === 'result_pass') {
    return (
      <div className="bg-card rounded-2xl border border-border p-8 sm:p-12 text-center animate-fade-in">
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={40} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-3">Congratulations! You qualified.</h2>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
          You passed the WinBounty AI Prompter application. Set your password to create your account and start competing.
        </p>
        <button
          onClick={() => setPhase('password_setup')}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-xl font-semibold hover:bg-secondary transition-all active:scale-95"
        >
          Set Up My Account <ChevronRight size={18} />
        </button>
      </div>
    );
  }

  // ── Result: Fail ──────────────────────────────────────────────────────────
  if (phase === 'result_fail') {
    return (
      <div className="bg-card rounded-2xl border border-border p-8 sm:p-12 text-center animate-fade-in">
        <div className="w-20 h-20 bg-danger/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle size={40} className="text-danger" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-3">Sorry, you did not qualify.</h2>
        <p className="text-muted-foreground mb-4 max-w-md mx-auto leading-relaxed">
          Thank you for applying to WinBounty. Unfortunately, your application did not meet the requirements.
        </p>
        <div className="bg-warning/10 border border-warning/20 rounded-xl p-4 mb-8 max-w-sm mx-auto">
          <div className="flex items-center gap-2 justify-center">
            <AlertTriangle size={16} className="text-warning flex-shrink-0" />
            <p className="text-sm text-warning-foreground font-medium">
              Your account is locked for 7 days.
            </p>
          </div>
        </div>
        {isGoogleUser ? (
          <Link href="/application-locked-out" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-xl font-medium hover:bg-secondary transition-colors">
            View Lockout Details &amp; Resources
          </Link>
        ) : (
          <Link href="/" className="inline-flex items-center gap-2 border border-border text-foreground px-6 py-2.5 rounded-xl font-medium hover:bg-muted transition-colors">
            Return to Homepage
          </Link>
        )}

        {/* ── Educational Section ─────────────────────────────────────────── */}
        <div className="mt-12 text-left">
          {/* Header */}
          <div className="border-t border-border pt-10 mb-8 text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              <span>📚</span> Learn &amp; Come Back Stronger
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">How to Qualify Next Time</h3>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm leading-relaxed">
              We encourage you to learn more about <span className="text-foreground font-medium">AI prompting for videos with speech</span>. The steps below will help you build the skills needed to qualify.
            </p>
          </div>

          {/* Workflow Steps — asymmetric bento layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">

            {/* Step 1 — Higgsfield (wide card) */}
            <div className="sm:col-span-2 bg-gradient-to-br from-primary/5 via-card to-card border border-primary/20 rounded-2xl p-6 flex flex-col sm:flex-row gap-5 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-xl">🚀</div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                  <span className="text-xs font-bold uppercase tracking-widest text-primary">Step 1 · All-in-One Platform</span>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Recommended</span>
                </div>
                <h4 className="text-base font-bold text-foreground mb-1">Higgsfield</h4>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  Access every AI tool you need under one affordable subscription. Higgsfield bundles image generation, video generation, and speech tools — so you can build a full workflow without juggling multiple expensive platforms.
                </p>
                <a
                  href="https://higgsfield.ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors group"
                >
                  Visit higgsfield.ai
                  <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                </a>
              </div>
            </div>

            {/* Step 2 — Nano Banana Pro */}
            <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-3 hover:border-primary/30 transition-colors">
              <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center text-lg">🍌</div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-amber-500 mb-1">Step 2 · Image Generation</div>
                <h4 className="text-base font-bold text-foreground mb-1">Nano Banana Pro</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Generate <span className="text-foreground font-medium">one image per line of script</span> or per clip. Build your full visual storyboard before you animate — this keeps your video cohesive and on-brand.
                </p>
              </div>
            </div>

            {/* Step 3 — Kling 3.0 */}
            <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-3 hover:border-primary/30 transition-colors">
              <div className="w-10 h-10 bg-violet-500/10 rounded-xl flex items-center justify-center text-lg">🎬</div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-violet-500 mb-1">Step 3 · Animate Your Images</div>
                <h4 className="text-base font-bold text-foreground mb-1">Kling 3.0</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Take every image you generated and bring it to life with <span className="text-foreground font-medium">Kling 3.0</span>. You can use any video generation tool, but Kling 3.0 is our top pick for quality and control.
                </p>
              </div>
            </div>

            {/* Step 4 — CapCut (wide card) */}
            <div className="sm:col-span-2 bg-gradient-to-br from-emerald-500/5 via-card to-card border border-emerald-500/20 rounded-2xl p-6 flex flex-col sm:flex-row gap-5 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center text-xl">✂️</div>
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-widest text-emerald-500 mb-1.5">Step 4 · Final Edit</div>
                <h4 className="text-base font-bold text-foreground mb-1">Stitch It Together in CapCut</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Once all your clips are animated, import them into <span className="text-foreground font-medium">CapCut</span> to stitch them into a single polished video. Add your speech audio, captions, and transitions to complete the final submission.
                </p>
              </div>
            </div>
          </div>

          {/* Tip footer */}
          <div className="bg-muted/50 rounded-xl px-5 py-4 flex gap-3 items-start">
            <span className="text-base flex-shrink-0 mt-0.5">💡</span>
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="text-foreground font-semibold">You are not locked into these tools.</span> Any image generation or video generation platform works — these are simply the ones we recommend for quality and value. Master the workflow, then reapply in 7 days.
            </p>
          </div>
        </div>
        {/* ── End Educational Section ─────────────────────────────────────── */}
      </div>
    );
  }

  // ── Password Setup (non-Google users who passed) ──────────────────────────
  if (phase === 'password_setup') {
    return (
      <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 animate-fade-in">
        <h2 className="text-xl font-bold text-foreground mb-1.5">Create your password</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Your account will be created as <span className="font-semibold text-foreground">@{phase1Data?.username}</span>
        </p>
        <form onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)} className="space-y-5">
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-foreground mb-1.5">Password</label>
            <p className="text-xs text-muted-foreground mb-2">Minimum 8 characters. Use a mix of letters, numbers, and symbols.</p>
            <div className="relative">
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${passwordForm.formState.errors.password ? 'border-danger' : 'border-input focus:border-primary'}`}
                placeholder="Create a strong password"
                {...passwordForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Password must be at least 8 characters' } })}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordForm.formState.errors.password && <p className="mt-1.5 text-xs text-danger">{passwordForm.formState.errors.password.message}</p>}
          </div>
          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-foreground mb-1.5">Confirm password</label>
            <div className="relative">
              <input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${passwordForm.formState.errors.confirm_password ? 'border-danger' : 'border-input focus:border-primary'}`}
                placeholder="Re-enter your password"
                {...passwordForm.register('confirm_password', { required: 'Please confirm your password', validate: (val) => val === passwordForm.getValues('password') || 'Passwords do not match' })}
              />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordForm.formState.errors.confirm_password && <p className="mt-1.5 text-xs text-danger">{passwordForm.formState.errors.confirm_password.message}</p>}
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 px-4 rounded-lg text-sm font-semibold hover:bg-secondary transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ minHeight: '42px' }}
          >
            {isLoading ? <><Loader2 size={16} className="animate-spin" />Creating account...</> : 'Create My Account & Start Competing'}
          </button>
        </form>
      </div>
    );
  }

  // ── Qualifying Phase ──────────────────────────────────────────────────────
  if (phase === 'qualifying') {
    if (loadingQuestions) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      );
    }

    // ── Confidentiality Gate (first step of qualifying) ───────────────────
    if (showConfidentialityStep) {
      return (
        <div className="space-y-6">
          {/* Progress header */}
          <div className="bg-card rounded-2xl border border-border p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h1 className="text-lg font-bold text-foreground">AI Prompter Application</h1>
                <p className="text-sm text-muted-foreground">Qualifying Test — Confidentiality Agreement</p>
              </div>
              <div className="flex items-center gap-3">
                {currentUserId && (
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="text-xs text-muted-foreground hover:text-danger border border-border hover:border-danger/40 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Sign out
                  </button>
                )}
                <span className="text-sm font-semibold text-primary">{progressPct}%</span>
              </div>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-primary h-2 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
            </div>
          </div>

          {/* Confidentiality card */}
          <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 animate-fade-in">
            <div className="mb-2">
              <h2 className="text-lg font-bold text-foreground leading-snug">Confidentiality agreement</h2>
            </div>

            <div className="bg-muted/40 border border-border rounded-xl p-4 my-4">
              <p className="text-sm text-foreground/80 leading-relaxed">
                Bounties, briefs, brand assets, and all content on WinBounty are confidential. You may use them only to create your WinBounty submissions. You may not share, repost, resell, or use them for any other person, brand, or platform.
              </p>
            </div>

            <p className="text-sm font-medium text-foreground mb-4">
              Do you agree to keep all briefs and content confidential and use them only for WinBounty?
            </p>

            <div className="space-y-2.5">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="confidentiality"
                  value="yes"
                  checked={confidentialityAnswer === 'yes'}
                  onChange={() => setConfidentialityAnswer('yes')}
                  className="w-4 h-4 accent-primary"
                />
                <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">Yes, I agree</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="confidentiality"
                  value="no"
                  checked={confidentialityAnswer === 'no'}
                  onChange={() => setConfidentialityAnswer('no')}
                  className="w-4 h-4 accent-danger"
                />
                <span className="text-sm font-medium text-foreground group-hover:text-danger transition-colors">No</span>
              </label>
            </div>

            {/* Disqualifier note — always visible */}
            <div className="mt-4 flex items-start gap-2 bg-muted/50 border border-border rounded-lg p-3">
              <AlertTriangle size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                Selecting &quot;No&quot; will disqualify your application. Agreeing to keep our content confidential is required to compete.
              </p>
            </div>

            {/* Warning when "No" is selected */}
            {confidentialityAnswer === 'no' && (
              <div className="mt-3 flex items-start gap-2 bg-danger/10 border border-danger/20 rounded-lg p-3">
                <AlertTriangle size={14} className="text-danger flex-shrink-0 mt-0.5" />
                <p className="text-xs text-danger font-medium">
                  You have selected &quot;No&quot;. Continuing will disqualify your application.
                </p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex gap-3 mt-8">
              <button
                type="button"
                onClick={handleQualifyingBack}
                className="flex items-center gap-2 border border-border text-foreground px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-muted transition-colors"
              >
                <ChevronLeft size={16} /> Back
              </button>
              <button
                type="button"
                onClick={handleConfidentialityNext}
                disabled={!confidentialityAnswer}
                className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 px-4 rounded-xl text-sm font-semibold hover:bg-secondary transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ minHeight: '44px' }}
              >
                {confidentialityAnswer === 'no' ? (
                  <>Submit Application <ChevronRight size={16} /></>
                ) : (
                  <>Continue <ChevronRight size={16} /></>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // No qualifying questions — pass directly (confidentiality already passed)
    if (qualifyingQuestions.length === 0) {
      storeApplicationAnswers();
      if (isGoogleUser) {
        setPhase('profile_setup');
      } else {
        setPhase('result_pass');
      }
      return null;
    }

    const currentQ = qualifyingQuestions[qualifyingStep];
    const selectedAnswer = qualifyingAnswers[currentQ.id];
    const qStepLabel = `Question ${qualifyingStep + 1} of ${qualifyingQuestions.length}`;

    return (
      <div className="space-y-6">
        {/* Progress header */}
        <div className="bg-card rounded-2xl border border-border p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-lg font-bold text-foreground">AI Prompter Application</h1>
              <p className="text-sm text-muted-foreground">
                Qualifying Test — {qStepLabel}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {currentUserId && (
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="text-xs text-muted-foreground hover:text-danger border border-border hover:border-danger/40 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Sign out
                </button>
              )}
              <span className="text-sm font-semibold text-primary">{progressPct}%</span>
            </div>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div className="bg-primary h-2 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Question card */}
        <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 animate-fade-in">
          <div className="mb-2">
            <h2 className="text-lg font-bold text-foreground leading-snug">{currentQ.question_text}</h2>
            <p className="text-xs text-muted-foreground mt-1">Select the correct answer</p>
          </div>

          <div className="space-y-2.5 mt-5">
            {currentQ.shuffledOptions.map((opt) => {
              const isSelected = selectedAnswer === opt.originalKey;
              return (
                <button
                  key={`q-opt-${currentQ.id}-${opt.key}`}
                  type="button"
                  onClick={() => handleQualifyingSelect(currentQ.id, opt.originalKey)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border text-sm text-left transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/5 text-foreground font-medium shadow-sm'
                      : 'border-border hover:border-primary/40 text-foreground hover:bg-muted/30'
                  }`}
                >
                  <span
                    className={`flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all ${
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-muted-foreground/40 text-muted-foreground'
                    }`}
                  >
                    {opt.key}
                  </span>
                  <span className="leading-snug">{opt.text}</span>
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex gap-3 mt-8">
            <button
              type="button"
              onClick={handleQualifyingBack}
              className="flex items-center gap-2 border border-border text-foreground px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-muted transition-colors"
            >
              <ChevronLeft size={16} /> Back
            </button>
            <button
              type="button"
              onClick={handleQualifyingNext}
              disabled={!selectedAnswer}
              className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 px-4 rounded-xl text-sm font-semibold hover:bg-secondary transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ minHeight: '44px' }}
            >
              {qualifyingStep < qualifyingQuestions.length - 1 ? (
                <>Next <ChevronRight size={16} /></>
              ) : (
                <>Submit Application <ChevronRight size={16} /></>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Phase 1 — one field at a time ─────────────────────────────────────────
  const stepLabel = `Question ${phase1Step + 1} of ${totalSteps}`;

  return (
    <div className="space-y-6">
      {/* Progress header */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-lg font-bold text-foreground">AI Prompter Application</h1>
            <p className="text-sm text-muted-foreground">
              Your Information — {stepLabel}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {currentUserId && (
              <button
                type="button"
                onClick={handleSignOut}
                className="text-xs text-muted-foreground hover:text-danger border border-border hover:border-danger/40 px-3 py-1.5 rounded-lg transition-colors"
              >
                Sign out
              </button>
            )}
            <span className="text-sm font-semibold text-primary">{progressPct}%</span>
          </div>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div className="bg-primary h-2 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Phase 1 — one field at a time */}
      <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 animate-fade-in">
        {isGoogleUser && phase1Step === 0 && (
          <div className="flex items-center gap-2.5 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 mb-5 text-sm">
            <CheckCircle2 size={16} className="text-primary flex-shrink-0" />
            <span className="text-foreground">Signed in with Google as <strong>{googleUserEmail}</strong></span>
          </div>
        )}

        {/* Step: full_name */}
        {phase1Steps[phase1Step].id === 'full_name' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold text-foreground mb-1">{phase1Steps[phase1Step].title}</h2>
              <p className="text-sm text-muted-foreground mb-5">Enter your full legal name as it appears on your ID.</p>

              {/* Google sign-in shortcut — only shown to non-Google users */}
              {!isGoogleUser && (
                <div className="mb-5">
                  <button
                    type="button"
                    onClick={async () => {
                      setIsGoogleSigningIn(true);
                      try {
                        await signInWithGoogle();
                      } catch {
                        setIsGoogleSigningIn(false);
                      }
                    }}
                    disabled={isGoogleSigningIn}
                    className="w-full flex items-center justify-center gap-3 border border-border bg-background hover:bg-muted text-foreground py-3 px-4 rounded-xl text-sm font-medium transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ minHeight: '44px' }}
                  >
                    {isGoogleSigningIn ? (
                      <><Loader2 size={16} className="animate-spin" />Redirecting to Google...</>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z" fill="#4285F4"/>
                          <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.3441 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z" fill="#34A853"/>
                          <path d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2822-1.71V4.9582H.9574A8.9961 8.9961 0 000 9c0 1.4523.3477 2.8268.9574 4.0418L3.964 10.71z" fill="#FBBC05"/>
                          <path d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9574 4.9582L3.964 7.29C4.6718 5.1627 6.6559 3.5795 9 3.5795z" fill="#EA4335"/>
                        </svg>
                        Sign in with Google to auto-fill
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground">or fill in manually</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                </div>
              )}

              <input
                id="full_name"
                type="text"
                autoFocus
                className={`w-full px-3.5 py-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${phase1Form.formState.errors.full_name ? 'border-danger' : 'border-input focus:border-primary'}`}
                placeholder="Kaito Nakamura"
                {...phase1Form.register('full_name', { required: 'Full name is required' })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePhase1Next(); } }}
              />
              {phase1Form.formState.errors.full_name && <p className="mt-1.5 text-xs text-danger">{phase1Form.formState.errors.full_name.message}</p>}
            </div>
          </div>
        )}

        {/* Step: email */}
        {phase1Steps[phase1Step].id === 'email' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold text-foreground mb-1">{phase1Steps[phase1Step].title}</h2>
              <p className="text-sm text-muted-foreground mb-5">We&apos;ll use this to contact you about your application.</p>
              <input
                id="app-email"
                type="email"
                autoFocus
                readOnly={isGoogleUser}
                className={`w-full px-3.5 py-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${isGoogleUser ? 'opacity-60 cursor-not-allowed' : ''} ${phase1Form.formState.errors.email ? 'border-danger' : 'border-input focus:border-primary'}`}
                placeholder="kaito@example.com"
                {...phase1Form.register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePhase1Next(); } }}
              />
              {phase1Form.formState.errors.email && <p className="mt-1.5 text-xs text-danger">{phase1Form.formState.errors.email.message}</p>}
            </div>
          </div>
        )}

        {/* Step: country */}
        {phase1Steps[phase1Step].id === 'country' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold text-foreground mb-1">{phase1Steps[phase1Step].title}</h2>
              <p className="text-sm text-muted-foreground mb-5">Used for admin review only. Not displayed publicly.</p>
              <select
                id="country"
                className={`w-full px-3.5 py-3 rounded-lg border text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${phase1Form.formState.errors.country ? 'border-danger' : 'border-input focus:border-primary'}`}
                {...phase1Form.register('country', { required: 'Country is required' })}
              >
                <option value="">Select your country</option>
                {COUNTRIES.map((c) => <option key={`country-${c}`} value={c}>{c}</option>)}
              </select>
              {phase1Form.formState.errors.country && <p className="mt-1.5 text-xs text-danger">{phase1Form.formState.errors.country.message}</p>}
            </div>
          </div>
        )}

        {/* Step: wise_tag (non-Google only) */}
        {phase1Steps[phase1Step].id === 'wise_tag' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold text-foreground mb-1">{phase1Steps[phase1Step].title}</h2>
              <p className="text-sm text-muted-foreground mb-5">Your Wise payment handle. Required for payouts. Stored encrypted.</p>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground select-none font-medium">@</span>
                <input
                  id="wise_tag"
                  type="text"
                  autoFocus
                  className={`w-full pl-8 pr-3.5 py-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${phase1Form.formState.errors.wise_tag ? 'border-danger' : 'border-input focus:border-primary'}`}
                  placeholder="yourwisename"
                  {...phase1Form.register('wise_tag', { required: 'Wise tag is required', pattern: { value: /^[a-zA-Z0-9._-]+$/, message: 'Enter your Wise ID without the @ symbol' } })}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePhase1Next(); } }}
                />
              </div>
              {phase1Form.formState.errors.wise_tag && <p className="mt-1.5 text-xs text-danger">{phase1Form.formState.errors.wise_tag.message}</p>}
            </div>
          </div>
        )}

        {/* Step: username (non-Google only) */}
        {phase1Steps[phase1Step].id === 'username' && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold text-foreground mb-1">{phase1Steps[phase1Step].title}</h2>
              <p className="text-sm text-muted-foreground mb-5">Unique, lowercase, no spaces. Visible on the leaderboard.</p>
              <input
                id="username"
                type="text"
                autoFocus
                className={`w-full px-3.5 py-3 rounded-lg border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors ${phase1Form.formState.errors.username ? 'border-danger' : 'border-input focus:border-primary'}`}
                placeholder="kaito_prompts"
                {...phase1Form.register('username', {
                  required: 'Username is required',
                  pattern: { value: /^[a-z0-9_]+$/, message: 'Lowercase letters, numbers, and underscores only' },
                  minLength: { value: 3, message: 'At least 3 characters' },
                  maxLength: { value: 30, message: 'Maximum 30 characters' },
                })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePhase1Next(); } }}
              />
              {phase1Form.formState.errors.username && <p className="mt-1.5 text-xs text-danger">{phase1Form.formState.errors.username.message}</p>}
            </div>
          </div>
        )}

        {/* Step: job_interest (optional, non-disqualifying) */}
        {phase1Steps[phase1Step].id === 'job_interest' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl font-bold text-foreground">{phase1Steps[phase1Step].title}</h2>
              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full font-medium">Optional</span>
            </div>
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-5">
              <p className="text-sm text-foreground/80 leading-relaxed mb-4">
                Top performers can get invited to a full-time AI Prompter role. This is an optional bonus, you can keep earning on bounties for as long as you like.
              </p>
              <p className="text-sm font-medium text-foreground mb-3">Are you open to a full-time role if invited?</p>
              <div className="space-y-2.5">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input type="radio" value="yes" className="w-4 h-4 accent-primary" {...phase1Form.register('job_interest', { required: 'Please select an option' })} />
                  <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">Yes, I&apos;m open to it if invited</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input type="radio" value="no" className="w-4 h-4 accent-primary" {...phase1Form.register('job_interest', { required: 'Please select an option' })} />
                  <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">No, I just want to earn on bounties</span>
                </label>
              </div>
              {phase1Form.formState.errors.job_interest && <p className="mt-2 text-xs text-danger">{phase1Form.formState.errors.job_interest.message}</p>}
              <p className="mt-3 text-xs text-muted-foreground">Either answer is fine — this does not affect your application.</p>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex gap-3 mt-8">
          {phase1Step > 0 && (
            <button
              type="button"
              onClick={handlePhase1Back}
              className="flex items-center gap-2 border border-border text-foreground px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-muted transition-colors"
            >
              <ChevronLeft size={16} /> Back
            </button>
          )}
          <button
            type="button"
            onClick={handlePhase1Next}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 px-4 rounded-xl text-sm font-semibold hover:bg-secondary transition-all active:scale-95 disabled:opacity-60"
            style={{ minHeight: '44px' }}
          >
            {isLoading ? (
              <><Loader2 size={16} className="animate-spin" />Loading questions...</>
            ) : phase1Steps[phase1Step].id === 'job_interest' ? (
              <>Continue to Qualifying Test <ChevronRight size={16} /></>
            ) : (
              <>Next <ChevronRight size={16} /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}